// Keycloak Onboarding Runbook - how each business stands up and connects its
// OWN Keycloak to FederateX (true-federation model). Linked from the Users &
// Roles page ("Set up Keycloak →"). Content mirrors the design spec §8.1.
import { Box, Typography, Paper, Divider, useTheme } from "@mui/material";
import { tokens } from "../../theme";
import ShieldIcon from "@mui/icons-material/Shield";

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

const Code = ({ children }) => (
  <Box component="code" sx={{ fontFamily: MONO, fontSize: "0.8125rem",
    backgroundColor: "rgba(0,135,180,0.08)", border: "1px solid rgba(0,135,180,0.2)",
    borderRadius: 1, px: 0.75, py: 0.1, color: "var(--fx-text-primary)" }}>
    {children}
  </Box>
);

const Block = ({ children }) => (
  <Box component="pre" sx={{ fontFamily: MONO, fontSize: "0.78rem", lineHeight: 1.55,
    backgroundColor: "var(--fx-subtle)", border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 2, p: 2, overflowX: "auto", color: "var(--fx-text-primary)", my: 1.5 }}>
    {children}
  </Box>
);

const Step = ({ n, title, children }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
        backgroundColor: colors.primary[500], color: "#fff", display: "flex",
        alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "0.9rem" }}>
        {n}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "var(--fx-text-strong)", mb: 0.75 }}>
          {title}
        </Typography>
        <Box sx={{ fontSize: "0.875rem", color: "var(--fx-text-secondary)", lineHeight: 1.6 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const KeycloakSetupGuide = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box sx={{ maxWidth: 880 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <ShieldIcon sx={{ color: colors.secondary[500] }} />
        <Typography variant="h4" sx={{ fontWeight: 700, color: "var(--fx-text-strong)" }}>
          Keycloak Onboarding Runbook
        </Typography>
      </Box>
      <Typography sx={{ color: "var(--fx-text-secondary)", mb: 3, lineHeight: 1.6 }}>
        FederateX is a federated platform: every business runs and controls its <strong>own
        Keycloak</strong>, and your roles and users live in <em>your</em> identity provider - not
        a shared one. This guide walks a business administrator through standing up a Keycloak
        realm and connecting it to FederateX so invites, sign-in, and role mapping work
        end-to-end. The FederateX backend talks to your Keycloak server-to-server; the browser
        never holds an admin credential.
      </Typography>

      <Paper sx={{ p: 2, mb: 3, borderLeft: `4px solid ${colors.secondary[500]}`,
                   backgroundColor: "var(--fx-paper)" }}>
        <Typography sx={{ fontSize: "0.8125rem", color: "var(--fx-text-secondary)" }}>
          <strong>You will hand FederateX five values</strong> at the end: issuer URL, realm name,
          the public frontend client id, and the confidential admin client’s id + secret. A
          platform superadmin registers them under <Code>Users &amp; Roles → register Keycloak
          connection</Code>, which runs an automatic preflight check.
        </Typography>
      </Paper>

      <Divider sx={{ mb: 3 }} />

      <Step n={1} title="Access your Keycloak admin console">
        Sign in to your Keycloak admin console (for self-hosted Keycloak this is typically{" "}
        <Code>https://&lt;your-keycloak-host&gt;/admin/</Code>). You need an account with the{" "}
        <Code>admin</Code> role on the <Code>master</Code> realm, or realm-admin rights on the
        realm you’ll use. FederateX supports Keycloak 24+ (the platform is validated on 26.x).
      </Step>

      <Step n={2} title="Create (or choose) your realm">
        Create a realm for your organization - e.g. <Code>acme</Code>. This realm is the
        boundary of your users and roles. Note the <strong>issuer URL</strong>, which is:
        <Block>{`https://<your-keycloak-host>/realms/<realm>`}</Block>
        FederateX trusts tokens from exactly this issuer and no other.
      </Step>

      <Step n={3} title="Create the public frontend client (login)">
        Under <Code>Clients → Create client</Code>, add a public client (e.g.{" "}
        <Code>federatex-frontend</Code>):
        <Block>{`Client type:        OpenID Connect
Client ID:          federatex-frontend
Client authentication: OFF   (public)
Standard flow:      ON
Valid redirect URIs: https://<your-federatex-host>/*
Web origins:        https://<your-federatex-host>`}</Block>
        Ensure realm roles are included in the token: the default{" "}
        <Code>realm roles</Code> mapper already emits <Code>realm_access.roles</Code>, which is
        what FederateX reads.
      </Step>

      <Step n={4} title="Create the confidential admin (service-account) client">
        FederateX uses this to manage users and roles in your realm. Create a second client
        (e.g. <Code>federatex-bff-admin</Code>):
        <Block>{`Client ID:             federatex-bff-admin
Client authentication: ON    (confidential)
Standard flow:         OFF
Service accounts:      ON    (client credentials)`}</Block>
        Then on the client’s <Code>Service account roles</Code> tab, assign these{" "}
        <Code>realm-management</Code> client roles (least privilege):
        <Block>{`manage-users     view-users     query-users
view-realm       manage-realm   (role create/delete)
view-events                     (Activity tab)`}</Block>
        Copy the client <strong>secret</strong> from the <Code>Credentials</Code> tab - you’ll
        hand this to FederateX (it is stored encrypted, server-side only).
      </Step>

      <Step n={5} title="Enable events (Activity tab)">
        Under <Code>Realm settings → Events</Code>, enable <Code>Login events</Code> and{" "}
        <Code>Admin events</Code> (with “Include representation” on). This powers the FederateX
        Activity feed; without it that tab is simply empty.
      </Step>

      <Step n={6} title="Configure email (invitations)">
        Under <Code>Realm settings → Email</Code>, configure your SMTP server so invitation
        emails can be sent. For branded, per-business sending from your own domain, you can
        instead provide sender credentials to FederateX later under{" "}
        <Code>Users &amp; Roles → Organization → Email</Code>.
      </Step>

      <Step n={7} title="Hand the connection details to FederateX">
        Give your FederateX platform superadmin these five values; they register the connection
        (which immediately runs a preflight that mints a token and lists users + roles, so any
        missing scope is caught now):
        <Block>{`issuer URL          https://<host>/realms/<realm>
realm               <realm>
frontend client id  federatex-frontend
admin client id     federatex-bff-admin
admin client secret <from step 4>`}</Block>
        Once registered, your superadmin can create roles in FederateX - each one is written to
        the FederateX catalog <em>and</em> created in your Keycloak realm - invite members, and
        assign roles. When a member accepts and signs in, their token comes from your Keycloak
        and carries the assigned role.
      </Step>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--fx-text-strong)", mb: 1 }}>
        Troubleshooting
      </Typography>
      <Box sx={{ fontSize: "0.875rem", color: "var(--fx-text-secondary)", lineHeight: 1.7 }}>
        <p><strong>Preflight fails with “cannot list users / read roles.”</strong> The admin
        client is missing a <Code>realm-management</Code> role from step 4 - re-check the Service
        account roles tab.</p>
        <p><strong>Sign-in works but the user has no access.</strong> Confirm the{" "}
        <Code>realm roles</Code> token mapper is enabled on the frontend client so{" "}
        <Code>realm_access.roles</Code> appears in the token.</p>
        <p><strong>Role created in FederateX shows “Keycloak sync failed.”</strong> Your Keycloak
        was unreachable or the admin client lacks <Code>manage-realm</Code>. Fix the scope/network
        and use <Code>Retry sync</Code> on the role card - the role is never lost.</p>
      </Box>
    </Box>
  );
};

export default KeycloakSetupGuide;
